import { Upload } from "lucide-react";
import { type FallbackProps } from "react-error-boundary";
import { ErrorCard } from "../ErrorCard";
import { Skeleton } from "../ui/skeleton";
import { type Document, DocumentCard, DocumentCardSkeleton } from "@/components/DocumentCard";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { getErrorMessage } from "@/lib/utils/errors";

interface DocumentTileProps {
  documents: Document[];
  title: string;
  onUpload?: () => void;
  onDelete?: (documentId: string) => Promise<void>;
}

export function DocumentTile({ documents, title, onUpload, onDelete }: DocumentTileProps) {
  return (
    <Card className="p-4 flex min-h-64 flex-col flex-1 gap-4">
      <div className="flex justify-between items-center">
        <CardTitle className="font-semibold text-xl">{title}</CardTitle>
        {onUpload && (
          <Button variant="muted" onClick={onUpload}>
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="flex flex-col flex-1 bg-muted-extralight h-full items-center justify-center text-center rounded-lg py-4">
          <div className="p-3 bg-gray-200 rounded-full inline-flex items-center justify-center">
            <Upload className="h-8 w-8 text-muted-foreground" />
          </div>
          <CardTitle className="text-foreground py-2">No documents uploaded</CardTitle>
          <CardDescription>Press 'Upload' to get started.</CardDescription>
        </div>
      ) : (
        <div className="flex flex-col space-y-4">
          {documents.map((document) => (
            <DocumentCard key={document.id} document={document} onDelete={onDelete} />
          ))}
        </div>
      )}
    </Card>
  );
}

export function DocumentTileError({ error }: FallbackProps) {
  return (
    <Card className="p-4 min-h-64 flex flex-col flex-1 gap-4">
      <div className="flex justify-between items-center">
        <CardTitle className="font-semibold text-xl">Documents (0)</CardTitle>
      </div>
      <ErrorCard title="Unable to load documents" description={getErrorMessage(error)} />
    </Card>
  );
}

export function DocumentTileSkeleton() {
  return (
    <Card className="p-4 flex min-h-64 flex-col flex-1 gap-4">
      <div className="flex justify-between items-center">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-9 w-24" />
      </div>
      <div className="flex flex-col space-y-4 mt-4">
        <DocumentCardSkeleton />
      </div>
    </Card>
  );
}
